import {
    PropsWithChildren,
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Invoice, InvoiceSortValue } from "@common/types/invoice";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { useQueryClient } from "@tanstack/react-query";
import { useInvoicesPageSortingCache } from "@hooks/view/useInvoicesPageSortingCache";
import {
    getInvoicesQueryKey,
    loadInvoicesPage,
    useInvoices,
} from "@api/invoice.api";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import type { PaginationType } from "@hooks/paginationMode";
import { useLoadMorePages } from "@hooks/useLoadMorePages";
import { useLoadMoreTableState } from "@hooks/useLoadMoreTableState";
import { subscribeToUTCDayChange } from "@hooks/useUTCTodayDateOnly";
import {
    toInvoiceApiFilters,
    useInvoicesView,
} from "../../contexts/InvoicesViewContext";
import { useSort } from "./useSort";
import {
    PaginatedInvoicesTableContext,
    type PaginatedInvoicesTableContextValue,
} from "./PaginatedInvoicesTable.context";

const PER_PAGE = 25;

const selectInvoices = (response: { data: Invoice[] }) => response.data;
const getInvoiceId = (invoice: Invoice) => invoice.id;

interface Props {
    overlayFilters?: InvoiceFilters;
    /** Numbered pages by default; the task view loads more on scroll. */
    paginationType?: PaginationType;
}

export const PaginatedInvoicesTableContextProvider = ({
    children,
    overlayFilters,
    paginationType = "pages",
}: PropsWithChildren<Props>) => {
    const isLoadMore = paginationType === "loadMore";
    const [cachedSorting, setCachedSorting] = useInvoicesPageSortingCache();
    const { filters } = useInvoicesView();
    const [pagedPage, setPagedPage] = useState(1);
    const page = isLoadMore ? 1 : pagedPage;
    const workspaceId = useActiveWorkspace()?.id;

    const { sortExpression, currentSort, handleSortChange } =
        useSort(cachedSorting);

    const apiFilters = useMemo(
        () => ({
            ...toInvoiceApiFilters(filters),
            ...overlayFilters,
        }),
        [filters, overlayFilters],
    );
    const apiFiltersKey = JSON.stringify(apiFilters);

    useEffect(() => {
        setPagedPage(1);
    }, [apiFiltersKey]);

    // In load-more mode this is the first batch, which carries the total.
    const invoicesResult = useInvoices({
        filters: apiFilters,
        sort: sortExpression,
        page,
        limit: PER_PAGE,
    });

    const loadedInvoices = useLoadMorePages({
        buildPageQuery: (pageNumber) => {
            const query = {
                filters: apiFilters,
                sort: sortExpression,
                page: pageNumber,
                limit: PER_PAGE,
            };
            return {
                queryKey: getInvoicesQueryKey(workspaceId, query),
                queryFn: () => loadInvoicesPage(workspaceId ?? "", query),
            };
        },
        select: selectInvoices,
        getRowId: getInvoiceId,
        pageSize: PER_PAGE,
        total: invoicesResult.data?.total,
        enabled: isLoadMore && !!workspaceId,
    });
    const loadMoreState = useLoadMoreTableState(loadedInvoices);
    const loadedRows = loadedInvoices.rows;
    const loadedQueryKeysRef = useLatestRef(loadedInvoices.loadedQueryKeys);

    const { data: invoices, isLoading, refetch } = invoicesResult;

    const queryClient = useQueryClient();
    useEffect(
        () =>
            subscribeToUTCDayChange(() => {
                if (!isLoadMore) {
                    void refetch();
                    return;
                }
                for (const queryKey of loadedQueryKeysRef.current) {
                    void queryClient.refetchQueries({ queryKey, exact: true });
                }
            }),
        [refetch, isLoadMore, loadedQueryKeysRef, queryClient],
    );

    const handleSortChangeWithPageReset = useCallback(
        (value: InvoiceSortValue) => {
            startTransition(() => {
                handleSortChange(value);
                setCachedSorting(value);
                setPagedPage(1);
            });
        },
        [handleSortChange, setCachedSorting],
    );

    const contextValue = useMemo<PaginatedInvoicesTableContextValue>(
        () => ({
            currentPage: page,
            totalPages: isLoadMore ? 1 : (invoices?.pageCount ?? 1),
            invoiceCount: invoices?.total ?? 0,
            invoices:
                isLoadMore && invoices ? (loadedRows ?? []) : invoices?.data,
            setPage: setPagedPage,
            handleSortChange: handleSortChangeWithPageReset,
            currentSort,
            isLoading,
            filters: apiFilters,
            loadMore: isLoadMore ? loadMoreState : undefined,
        }),
        [
            page,
            isLoadMore,
            invoices,
            loadedRows,
            handleSortChangeWithPageReset,
            currentSort,
            isLoading,
            apiFilters,
            loadMoreState,
        ],
    );

    return (
        <PaginatedInvoicesTableContext.Provider value={contextValue}>
            {children}
        </PaginatedInvoicesTableContext.Provider>
    );
};
